import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, Phone, Calendar, Plus, Pencil, UserPlus, Eye, RefreshCw, X,
  MapPin, Loader2, Ban, ChevronDown, Trash2, Clock, Info, ArrowRight,
  CheckCircle2, Activity, Navigation, MoreHorizontal, AlertTriangle, Truck, UserCheck, User, Ambulance,
  Copy, Check, SlidersHorizontal, Inbox
} from 'lucide-react';
import { PIPELINE_STAGES, PIPELINE_STAGE_GROUPS, ACTIVE_TRIP_SUBSTAGES, deriveTripStage } from './mockTrackingData';
import commandCenterService from '../../services/commandCenterService';
import CommandCenterTripMap from './CommandCenterTripMap';
import CommandCenterAssignDriverModal from './CommandCenterAssignDriverModal';
import CommandCenterTripDetailsModal from './CommandCenterTripDetailsModal';
import CommandCenterDateFilter from './CommandCenterDateFilter';
import SlaTimer, {
  formatDuration,
  getTripStartTime,
  getActiveStageTimerConfig,
  getTripLegBreakdown,
} from './SlaTimer';

// Truly closed out — no further edits, no re-dispatch, driver info is
// view-only. Rejected is deliberately NOT here: per the backend's own
// stageToWhere mapping it's really just a PENDING booking with driver_id
// cleared after a decline/timeout, functionally identical to Pending —
// re-assigning and cancelling both still make sense there (previously the
// Actions column showed a "Closed" badge here while the Driver column right
// next to it still offered a live Assign button, which was misleading).
const TERMINAL_STAGES = ['Completed', 'Cancelled'];
// Cancel only makes sense before the patient is actually in the vehicle —
// once a driver has arrived at pickup (At Pickup, Towards Drop, At Drop), there's
// no way to "un-happen" it from here. Matches the same rule enforced server-side in
// commandCenterDispatchService.cancelBooking, which checks arrived_at.
const CANCELLABLE_STAGES = ['Requested', 'Pending', 'Scheduled', 'Towards Pickup', 'Untracked', 'Rejected'];
const ACTIONS_BADGE_CLASS = {
  'Completed': 'bg-green-50 text-green-700',
  'Cancelled': 'bg-red-50 text-red-600',
};
const ACTIONS_BADGE_LABEL = {
  'Completed': 'Done',
  'Cancelled': 'Closed',
};

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const formatWhen = (isoString) => {
  const d = new Date(isoString);
  const day = ordinal(d.getDate());
  const month = d.toLocaleString('en-US', { month: 'short' });
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${day} ${month} ${d.getFullYear()}, ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
};

const STATUS_BADGE_CLASS = {
  'Requested': 'bg-violet-50 text-violet-700',
  'Pending': 'bg-gray-100 text-gray-700',
  'Scheduled': 'bg-slate-100 text-slate-700',
  'Active Trips': 'bg-blue-50 text-blue-700',
  'Towards Pickup': 'bg-blue-50 text-blue-700',
  'At Pickup': 'bg-cyan-50 text-cyan-700',
  'Towards Drop': 'bg-indigo-50 text-indigo-700',
  'At Drop': 'bg-purple-50 text-purple-700',
  'Untracked': 'bg-amber-50 text-amber-700',
  'Completed': 'bg-green-50 text-green-700',
  'Cancelled': 'bg-red-50 text-red-700',
  'Rejected': 'bg-amber-50 text-amber-800',
};

const NEXT_STAGE_CONFIG = {
  'Scheduled': {
    target: 'Towards Pickup',
    label: 'Move to Towards Pickup',
    buttonClass: 'border-blue-500 text-blue-600 hover:bg-blue-50',
    badgeClass: 'border-blue-300 bg-blue-50/80 text-blue-700 hover:bg-blue-100/80 hover:border-blue-400',
    icon: ArrowRight,
  },
  'Towards Pickup': {
    target: 'At Pickup',
    label: 'Move to At Pickup',
    buttonClass: 'border-cyan-500 text-cyan-700 hover:bg-cyan-50',
    badgeClass: 'border-cyan-300 bg-cyan-50/80 text-cyan-700 hover:bg-cyan-100/80 hover:border-cyan-400',
    icon: ArrowRight,
  },
  'At Pickup': {
    target: 'Towards Drop',
    label: 'Move to Towards Drop',
    buttonClass: 'border-indigo-500 text-indigo-700 hover:bg-indigo-50',
    badgeClass: 'border-indigo-300 bg-indigo-50/80 text-indigo-700 hover:bg-indigo-100/80 hover:border-indigo-400',
    icon: ArrowRight,
  },
  'Towards Drop': {
    target: 'At Drop',
    label: 'Move to At Drop',
    buttonClass: 'border-purple-500 text-purple-700 hover:bg-purple-50',
    badgeClass: 'border-purple-300 bg-purple-50/80 text-purple-700 hover:bg-purple-100/80 hover:border-purple-400',
    icon: ArrowRight,
  },
  'At Drop': {
    target: 'Completed',
    label: 'Complete Trip',
    buttonClass: 'border-emerald-500 text-emerald-700 hover:bg-emerald-50',
    badgeClass: 'border-emerald-300 bg-emerald-50/80 text-emerald-700 hover:bg-emerald-100/80 hover:border-emerald-400',
    icon: CheckCircle2,
  },
};

const VALID_NEXT_STAGES = {
  'Requested': ['Towards Pickup', 'Scheduled'],
  'Pending': ['Towards Pickup', 'Scheduled'],
  'Scheduled': ['Towards Pickup', 'At Pickup', 'Towards Drop', 'At Drop', 'Completed'],
  'Towards Pickup': ['At Pickup', 'Towards Drop', 'At Drop', 'Completed'],
  'At Pickup': ['Towards Drop', 'At Drop', 'Completed'],
  'Towards Drop': ['At Drop', 'Completed'],
  'At Drop': ['Completed'],
  'Untracked': ['Towards Pickup', 'At Pickup', 'Towards Drop', 'At Drop', 'Completed'],
};

const getNextStageAction = (trip) => {
  if (!trip) return null;
  const stage = trip.stage;
  if (NEXT_STAGE_CONFIG[stage]) {
    return NEXT_STAGE_CONFIG[stage];
  }
  if (stage === 'Untracked') {
    if (trip.atDropoffAt || trip.rawBooking?.status === 'AT_DROPOFF') {
      return { target: 'Completed', label: 'Complete Trip', buttonClass: 'border-emerald-500 text-emerald-700 hover:bg-emerald-50', badgeClass: 'border-emerald-300 bg-emerald-50/80 text-emerald-700 hover:bg-emerald-100/80 hover:border-emerald-400', icon: CheckCircle2 };
    }
    if (trip.rawBooking?.status === 'ARRIVED' || (trip.arrivedAt && trip.rawBooking?.status === 'IN_PROGRESS')) {
      return { target: 'At Drop', label: 'Move to At Drop', buttonClass: 'border-purple-500 text-purple-700 hover:bg-purple-50', badgeClass: 'border-purple-300 bg-purple-50/80 text-purple-700 hover:bg-purple-100/80 hover:border-purple-400', icon: ArrowRight };
    }
    if (trip.arrivedAt || trip.rawBooking?.status === 'AT_PICKUP') {
      return { target: 'Towards Drop', label: 'Move to Towards Drop', buttonClass: 'border-indigo-500 text-indigo-700 hover:bg-indigo-50', badgeClass: 'border-indigo-300 bg-indigo-50/80 text-indigo-700 hover:bg-indigo-100/80 hover:border-indigo-400', icon: ArrowRight };
    }
    return { target: 'At Pickup', label: 'Move to At Pickup', buttonClass: 'border-cyan-500 text-cyan-700 hover:bg-cyan-50', badgeClass: 'border-cyan-300 bg-cyan-50/80 text-cyan-700 hover:bg-cyan-100/80 hover:border-cyan-400', icon: ArrowRight };
  }
  return null;
};

const SUBSTAGE_FILTER_CONFIG = {
  'Towards Pickup': {
    icon: Navigation,
    iconClass: 'text-blue-500',
    iconActiveClass: 'text-white',
    activeClass: 'bg-blue-600 text-white border-blue-600 shadow-sm font-semibold',
    badgeActiveClass: 'bg-white/25 text-white font-bold',
  },
  'At Pickup': {
    icon: User,
    iconClass: 'text-cyan-500',
    iconActiveClass: 'text-white',
    activeClass: 'bg-blue-600 text-white border-blue-600 shadow-sm font-semibold',
    badgeActiveClass: 'bg-white/25 text-white font-bold',
  },
  'Towards Drop': {
    icon: Truck,
    iconClass: 'text-indigo-500',
    iconActiveClass: 'text-white',
    activeClass: 'bg-blue-600 text-white border-blue-600 shadow-sm font-semibold',
    badgeActiveClass: 'bg-white/25 text-white font-bold',
  },
  'At Drop': {
    icon: MapPin,
    iconClass: 'text-rose-500',
    iconActiveClass: 'text-white',
    activeClass: 'bg-blue-600 text-white border-blue-600 shadow-sm font-semibold',
    badgeActiveClass: 'bg-white/25 text-white font-bold',
  },
  'Untracked': {
    icon: AlertTriangle,
    iconClass: 'text-purple-500',
    iconActiveClass: 'text-white',
    activeClass: 'bg-blue-600 text-white border-blue-600 shadow-sm font-semibold',
    badgeActiveClass: 'bg-white/25 text-white font-bold',
  },
};

const renderPipelineStageIcon = (stage, isActive = false) => {
  switch (stage) {
    case 'Active Trips':
      return (
        <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 shadow-xs ${isActive ? 'bg-white/20 text-white' : 'bg-indigo-50 text-indigo-600'}`}>
          <Navigation className={`w-3 h-3 ${isActive ? 'text-white fill-white' : 'text-indigo-600 fill-indigo-600'}`} />
        </span>
      );
    case 'Scheduled':
      return (
        <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 shadow-xs ${isActive ? 'bg-white/20 text-white' : 'bg-blue-50 text-blue-600'}`}>
          <Calendar className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-blue-600'}`} />
        </span>
      );
    case 'Requested':
      return (
        <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 shadow-xs ${isActive ? 'bg-white/20 text-white' : 'bg-violet-50 text-violet-600'}`}>
          <Inbox className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-violet-600'}`} />
        </span>
      );
    case 'Pending':
      return (
        <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 shadow-xs ${isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`}>
          <Clock className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-gray-600'}`} />
        </span>
      );
    case 'Completed':
      return (
        <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 shadow-xs ${isActive ? 'bg-white/20 text-white' : 'bg-emerald-50 text-emerald-600'}`}>
          <CheckCircle2 className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-emerald-600'}`} />
        </span>
      );
    case 'Cancelled':
      return (
        <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 shadow-xs ${isActive ? 'bg-white/20 text-white' : 'bg-red-50 text-red-600'}`}>
          <Ban className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-red-600'}`} />
        </span>
      );
    case 'Rejected':
      return (
        <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 shadow-xs ${isActive ? 'bg-white/20 text-white' : 'bg-amber-50 text-amber-700'}`}>
          <AlertTriangle className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-amber-700'}`} />
        </span>
      );
    default:
      return null;
  }
};

const PIPELINE_STAGE_STYLE = {
  'Requested': {
    labelColor: 'text-violet-700',
    countColor: 'text-violet-700 font-semibold',
    defaultBg: 'bg-white border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800',
    activeBg: 'bg-violet-600 text-white border-violet-600 shadow-sm font-semibold',
    badgeDefault: 'bg-violet-50 text-violet-700 border border-violet-100 font-bold',
    badgeActive: 'bg-white/25 text-white font-bold',
  },
  'Active Trips': {
    labelColor: 'text-gray-700',
    countColor: 'text-gray-500 font-semibold',
    defaultBg: 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900',
    activeBg: 'bg-blue-600 text-white border-blue-600 shadow-sm font-semibold',
    badgeDefault: 'bg-gray-100 text-gray-600',
    badgeActive: 'bg-white/25 text-white font-bold',
  },
  'Scheduled': {
    labelColor: 'text-gray-700',
    countColor: 'text-gray-500 font-semibold',
    defaultBg: 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900',
    activeBg: 'bg-blue-600 text-white border-blue-600 shadow-sm font-semibold',
    badgeDefault: 'bg-gray-100 text-gray-600',
    badgeActive: 'bg-white/25 text-white font-bold',
  },
  'Pending': {
    labelColor: 'text-gray-700',
    countColor: 'text-gray-500 font-semibold',
    defaultBg: 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900',
    activeBg: 'bg-blue-600 text-white border-blue-600 shadow-sm font-semibold',
    badgeDefault: 'bg-gray-100 text-gray-600',
    badgeActive: 'bg-white/25 text-white font-bold',
  },
  'Completed': {
    labelColor: 'text-gray-700',
    countColor: 'text-gray-500 font-semibold',
    defaultBg: 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900',
    activeBg: 'bg-emerald-600 text-white border-emerald-600 shadow-sm font-semibold',
    badgeDefault: 'bg-gray-100 text-gray-600',
    badgeActive: 'bg-white/25 text-white font-bold',
  },
  'Cancelled': {
    labelColor: 'text-red-700',
    countColor: 'text-red-700 font-semibold',
    defaultBg: 'bg-white border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800',
    activeBg: 'bg-red-600 text-white border-red-600 shadow-sm font-semibold',
    badgeDefault: 'bg-red-50 text-red-700 border border-red-100 font-bold',
    badgeActive: 'bg-white/25 text-white font-bold',
  },
  'Rejected': {
    labelColor: 'text-amber-800',
    countColor: 'text-amber-800 font-semibold',
    defaultBg: 'bg-white border-amber-200 text-amber-800 hover:bg-amber-50 hover:text-amber-900',
    activeBg: 'bg-amber-600 text-white border-amber-600 shadow-sm font-semibold',
    badgeDefault: 'bg-amber-50 text-amber-800 border border-amber-100 font-bold',
    badgeActive: 'bg-white/25 text-white font-bold',
  },
};

// Matches the fixed 3-term tracking vocabulary: Tracked (live GPS from the
// driver app), GPS Offline (has the app, but no current signal), Untracked
// (assigned by call — never had app-based tracking to begin with).
const MOTION_BADGE = {
  live: { label: 'Tracked', className: 'bg-green-100 text-green-700' },
  idle: { label: 'GPS Offline', className: 'bg-amber-100 text-amber-700' },
  untracked: { label: 'Untracked', className: 'bg-gray-100 text-gray-600' },
};

const PRIORITY_BADGE_CLASS = {
  EMERGENCY: 'bg-red-50 text-red-600 border border-red-200',
  NON_EMERGENCY: 'bg-gray-100 text-gray-600 border border-gray-200',
};

// Exception stages (Cancelled/Rejected) are exits off the main pipeline, not a
// step after Completed — styled as muted/outlined instead of the solid brand
// pill so the stepper doesn't imply they come next in sequence.
const EXCEPTION_STAGE_CLASS = {
  'Cancelled': { active: 'bg-red-50 text-red-700 border border-red-200', row: 'bg-red-50 text-red-700', dot: 'bg-red-500' },
  'Rejected': { active: 'bg-rose-50 text-rose-700 border border-rose-200', row: 'bg-rose-50 text-rose-700', dot: 'bg-rose-500' },
};

// Maps a real backend booking (commandCenterDispatchService.listBookings, via
// bookingIncludes) into the trip shape this page's UI already renders.
const mapBookingToTripUI = (booking, stage) => {
  const statusHistory = Array.isArray(booking.BookingStatusHistory) ? booking.BookingStatusHistory : [];
  const acceptedHistory = statusHistory.find(
    (h) => h.new_status === 'CONFIRMED' || h.new_status === 'ASSIGNED' || h.new_status === 'IN_PROGRESS'
  );
  const towardsPickupStart =
    acceptedHistory?.created_at ||
    booking.BookingTrackingSessions?.start_time ||
    booking.Trip?.start_time ||
    (booking.booking_type === 'SCHEDULED' ? booking.scheduled_datetime : null) ||
    booking.created_at;

  const arrivedAt = booking.arrived_at || null;

  const departedHistory = [...statusHistory].reverse().find(
    (h) => h.new_status === 'ARRIVED' || (h.old_status === 'AT_PICKUP' && (h.new_status === 'IN_PROGRESS' || h.new_status === 'ARRIVED'))
  );
  const departedPickupAt = departedHistory?.created_at || (stage === 'Towards Drop' ? (arrivedAt || booking.updated_at) : null);
  const completedAt = booking.Trip?.end_time || (booking.status === 'COMPLETED' ? booking.updated_at : null);

  let actualDurationMinutes = booking.actual_duration_minutes ?? booking.BookingTrackingSessions?.total_duration ?? null;
  if (actualDurationMinutes == null && (booking.status === 'COMPLETED' || stage === 'Completed')) {
    const startMs = new Date(towardsPickupStart || booking.created_at).getTime();
    const endMs = new Date(completedAt || booking.updated_at || Date.now()).getTime();
    if (endMs > startMs && isFinite(startMs) && isFinite(endMs)) {
      actualDurationMinutes = Math.max(1, Math.round((endMs - startMs) / 60000));
    }
  }

  const derivedStage = deriveTripStage(booking);
  const tripStage = stage === 'Active Trips' ? derivedStage : (stage || derivedStage);

  return {
    id: booking.booking_id,
    stage: tripStage,
    rawBooking: booking,
    customer: {
      name: booking.patient_name || 'Unknown',
      phone: booking.patient_phone || '—',
      phoneCountryCode: booking.patient_phone_country_code || null,
      age: booking.patient_age || null,
      gender: booking.patient_gender || null,
      email: booking.patient_email || null,
      condition: booking.patient_condition || '—',
      contactPersonName: booking.contact_person_name || null,
      contactPersonPhone: booking.contact_person_phone || null,
      hmisId: booking.hmis_id || null,
    },
    driver: booking.driver ? {
      id: booking.driver.driver_id,
      name: booking.driver.name,
      phone: booking.driver.phone,
      phoneCountryCode: booking.driver.phone_country_code || null,
      email: booking.driver.email || null,
      licenseNumber: booking.driver.license_number || null,
      experienceLevel: booking.driver.experience_level || null,
      vehiclePlate: booking.vehicle?.plate_number || '—',
      vehicleType: booking.vehicle?.type?.name || '—',
      operatorName: booking.vehicle?.operator?.name || '—',
      operatorContact: booking.vehicle?.operator?.contact_info || null,
      isVerified: booking.driver.is_verified,
      offPlatform: !booking.is_app_tracked,
      location: booking.vehicle?.current_latitude != null && booking.vehicle?.current_longitude != null ? {
        latitude: parseFloat(booking.vehicle.current_latitude),
        longitude: parseFloat(booking.vehicle.current_longitude),
      } : null,
    } : null,
    vehicle: booking.vehicle ? {
      id: booking.vehicle.vehicle_id,
      plateNumber: booking.vehicle.plate_number,
      type: booking.vehicle.type?.name,
      operator: booking.vehicle.operator?.name,
      isVerified: booking.vehicle.is_verified,
    } : null,
    hospital: booking.hospital ? {
      id: booking.hospital.hospital_id,
      name: booking.hospital.name,
      city: booking.hospital.city,
      state: booking.hospital.state,
      address: booking.hospital.address,
      contactPerson: booking.hospital.contact_person,
      contactPhone: booking.hospital.contact_person_phone,
    } : (booking.hospital_name_text ? { name: booking.hospital_name_text } : null),
    fare: {
      estimatedFare: booking.estimated_fare,
      finalFare: booking.final_fare,
      paymentStatus: booking.payment_status || 'PENDING',
      paymentMethod: booking.payment_method || null,
    },
    distance: {
      estimatedKm: booking.estimated_distance_km,
      actualKm: booking.actual_distance_km,
    },
    duration: {
      estimatedMin: booking.estimated_duration_minutes,
      actualMin: actualDurationMinutes,
    },
    driverMotion: !booking.driver ? null : (booking.is_app_tracked ? 'live' : 'untracked'),
    serviceCategory: booking.service_type || booking.source_channel || 'General',
    service: booking.vehicle?.type?.name || (booking.requested_vehicle_types || [])[0] || '—',
    requestedVehicleTypes: Array.isArray(booking.requested_vehicle_types) && booking.requested_vehicle_types.length > 0
      ? booking.requested_vehicle_types
      : (booking.vehicle?.type?.name ? [booking.vehicle.type.name] : []),
    priority: booking.priority_level === 'EMERGENCY' ? 'EMERGENCY' : 'NON_EMERGENCY',
    condition: booking.patient_condition || '—',
    when: {
      label: booking.booking_type === 'SCHEDULED' ? 'Scheduled' : 'Requested',
      at: booking.scheduled_datetime || booking.created_at,
    },
    pickup: booking.pickup_latitude != null ? {
      latitude: parseFloat(booking.pickup_latitude),
      longitude: parseFloat(booking.pickup_longitude),
      address: booking.pickup_address,
    } : null,
    dropoff: booking.dropoff_latitude != null ? {
      latitude: parseFloat(booking.dropoff_latitude),
      longitude: parseFloat(booking.dropoff_longitude),
      address: booking.dropoff_address,
    } : null,
    towardsPickupStart,
    arrivedAt,
    departedPickupAt,
    atDropoffAt: booking.at_dropoff_at || null,
    completedAt,
    bookingType: booking.booking_type || null,
    scheduledDatetime: booking.scheduled_datetime || null,
    createdAt: booking.created_at || null,
    updatedAt: booking.updated_at || null,
    trackingStartTime: booking.BookingTrackingSessions?.start_time || booking.Trip?.start_time || null,
    actualDurationMinutes,
    tripRecord: booking.Trip || null,
    cancelReason: (booking.BookingStatusHistory || []).slice().reverse().find((h) => String(h.new_status).toUpperCase() === 'CANCELLED' && h.reason)?.reason || booking.cancellation_reason || null,
    cancelledAt: (booking.BookingStatusHistory || []).slice().reverse().find((h) => String(h.new_status).toUpperCase() === 'CANCELLED')?.created_at || (booking.status === 'CANCELLED' ? booking.updated_at : null),
  };
};

const CopyablePhone = ({
  phone,
  callLabel = 'Call',
  showIcon = true,
  iconSize = 'w-2.5 h-2.5',
  textColor = 'text-blue-600 hover:text-blue-800',
  className = '',
  callClassName = '',
}) => {
  const [copied, setCopied] = useState(false);

  if (!phone || phone === '—' || phone === 'Not provided') {
    return <span className="text-gray-400 text-xs">—</span>;
  }

  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(String(phone).trim());
      }
    } catch (err) {
      console.warn('Clipboard write failed:', err);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className={`inline-flex items-center gap-1 group/copyable ${className}`}>
      <a
        href={`tel:${phone}`}
        onClick={(e) => e.stopPropagation()}
        className={`inline-flex items-center gap-1 ${textColor} hover:underline transition-colors ${callClassName}`}
        title={`${callLabel}: ${phone}`}
      >
        {showIcon && <Phone className={`${iconSize} shrink-0`} />}
        <span>{phone}</span>
      </a>
      <button
        type="button"
        onClick={handleCopy}
        className={`inline-flex items-center justify-center p-0.5 rounded transition-all cursor-pointer ${
          copied
            ? 'text-emerald-600 bg-emerald-50 scale-105'
            : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50/80 active:scale-95'
        }`}
        title={copied ? 'Copied to clipboard!' : `Copy ${phone}`}
        aria-label="Copy phone number"
      >
        {copied ? (
          <Check className={`${iconSize} stroke-[2.5] text-emerald-600`} />
        ) : (
          <Copy className={`${iconSize} opacity-70 group-hover/copyable:opacity-100 transition-opacity`} />
        )}
      </button>
    </div>
  );
};

// Automated alert mechanism for scheduled rides:
// Detects whether a scheduled ride is approaching its scheduled pickup time (threshold: <= 60 minutes).
// Excludes cancelled and completed rides.
export const getScheduledPickupAlert = (trip) => {
  if (!trip) return null;
  const terminalStages = ['Completed', 'Cancelled', 'Rejected'];
  if (terminalStages.includes(trip.stage)) return null;

  const isScheduled = trip.stage === 'Scheduled' || trip.bookingType === 'SCHEDULED' || trip.when?.label === 'Scheduled';
  if (!isScheduled) return null;

  // If driver is already assigned and active moving in later stage
  if (trip.driver && trip.stage !== 'Scheduled') return null;

  const scheduledTimeStr = trip.scheduledDatetime || (trip.when?.label === 'Scheduled' ? trip.when?.at : null);
  if (!scheduledTimeStr) return null;

  const scheduledMs = new Date(scheduledTimeStr).getTime();
  if (isNaN(scheduledMs)) return null;

  const diffMs = scheduledMs - Date.now();
  const diffMinutes = Math.round(diffMs / 60000);

  // Trigger when 1 hour (<= 60 mins) or less remains before pickup (and up to 2 hours overdue if unassigned)
  if (diffMinutes <= 60 && diffMinutes >= -120) {
    return {
      isUrgent: true,
      diffMinutes,
      isOverdue: diffMinutes < 0,
      scheduledTime: scheduledTimeStr,
      label: diffMinutes <= 0
        ? 'Pickup Due Now'
        : diffMinutes < 60
          ? `Pickup in ${diffMinutes}m`
          : 'Pickup in 1h',
    };
  }

  return null;
};

// Gentle audio alert chime for urgent scheduled rides using native Web Audio API
export const playScheduledAlertChime = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
    osc.frequency.setValueAtTime(880, ctx.currentTime + 0.15); // A5
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
  } catch {
    // Audio blocked by browser policy until interaction or unsupported
  }
};

const CommandCenterTrackingSimulator = ({ initialStage, onCreateBooking, onAction, onError, onWarning }) => {
  // Lands on whichever tab the action that brought the dispatcher here
  // actually resulted in (see deriveTripStage) — e.g. a just-accepted
  // booking opens straight on Towards Pickup instead of always Pending.
  // Falls back to Pending for an unset/unrecognized value; this component
  // remounts fresh each time the sidebar switches to Trips (CommandCenterDashboard
  // only renders one section at a time), so a plain useState initializer is
  // enough — no effect needed to react to a later prop change.
  const [activeStage, setActiveStage] = useState(
    initialStage && PIPELINE_STAGES.includes(initialStage)
      ? (ACTIVE_TRIP_SUBSTAGES.includes(initialStage) ? 'Active Trips' : initialStage)
      : 'Active Trips'
  );
  const [activeTripFilter, setActiveTripFilter] = useState(
    initialStage && ACTIVE_TRIP_SUBSTAGES.includes(initialStage) ? initialStage : 'All'
  );
  const [transitioningTripId, setTransitioningTripId] = useState(null);
  const [activeRowMenu, setActiveRowMenu] = useState(null);
  const [search, setSearch] = useState('');
  const [universalSearchResults, setUniversalSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const searchDebounceRef = useRef(null);
  const latestSearchQueryRef = useRef('');
  const [viewingTrip, setViewingTrip] = useState(null);
  const [infoTrip, setInfoTrip] = useState(null);
  const [viewingMapTrip, setViewingMapTrip] = useState(null);
  const [driverModal, setDriverModal] = useState(null); // { trip, mode: 'assign' | 'change' }
  const [cancelModal, setCancelModal] = useState(null); // { trip }
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null); // { trip }
  const [deleting, setDeleting] = useState(false);
  const [stageModal, setStageModal] = useState(null); // { trip, currentStage, targetStage, allowedTargets, action }
  const [stageModalNotes, setStageModalNotes] = useState('');
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [totalTrips, setTotalTrips] = useState(0);
  const [searchTotal, setSearchTotal] = useState(0);
  const [stageCounts, setStageCounts] = useState({});
  const [dateFilter, setDateFilter] = useState({
    period: 'all',
    startDate: null,
    endDate: null,
    label: 'All Time',
  });
  const dateFilterRef = useRef(dateFilter);
  dateFilterRef.current = dateFilter;

  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;

  const tripsLengthRef = useRef(trips.length);
  tripsLengthRef.current = trips.length;

  useEffect(() => {
    const handleCloseMenu = () => setActiveRowMenu(null);
    window.addEventListener('click', handleCloseMenu);
    return () => window.removeEventListener('click', handleCloseMenu);
  }, []);

  const handleTransitionStage = async (trip, targetStage, notes) => {
    if (!trip?.id || transitioningTripId) return;
    setTransitioningTripId(trip.id);
    try {
      const res = await commandCenterService.transitionTripStage(trip.id, { targetStage, notes });
      const updatedBooking = res.data?.booking || res.data;
      if (updatedBooking) {
        const updatedTrip = mapBookingToTripUI(updatedBooking, activeStage);
        setTrips((prev) => prev.map((t) => (t.id === trip.id ? updatedTrip : t)));
      } else {
        loadStage(activeStage, { silent: true });
      }
      loadAllStageCounts();
      handleAction?.(`Advanced trip #${trip.id} to ${targetStage}`);
      setStageModal(null);
      setStageModalNotes('');
    } catch (err) {
      onErrorRef.current?.(err.message || 'Failed to transition trip stage');
    } finally {
      setTransitioningTripId(null);
    }
  };
  // Tracks the most recently REQUESTED stage — if the dispatcher switches
  // tabs again before an in-flight listBookings response lands, an
  // out-of-order response (e.g. Pending resolving after a later Completed
  // request) would otherwise overwrite the table with the wrong stage's rows.
  const latestStageRequestRef = useRef(null);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onWarningRef = useRef(onWarning);
  onWarningRef.current = onWarning;

  const [urgentScheduledCount, setUrgentScheduledCount] = useState(0);
  const alertedScheduledIdsRef = useRef(new Set());

  // Scans trips to detect scheduled rides approaching pickup within <= 60 minutes
  const checkApproachingScheduledRides = useCallback((tripsToCheck) => {
    if (!Array.isArray(tripsToCheck) || tripsToCheck.length === 0) return;
    let count = 0;

    tripsToCheck.forEach((trip) => {
      const alert = getScheduledPickupAlert(trip);
      if (alert && alert.isUrgent) {
        count++;
        // Trigger notification and sound only once per trip threshold
        if (!alertedScheduledIdsRef.current.has(trip.id)) {
          alertedScheduledIdsRef.current.add(trip.id);
          const patientName = trip.customer?.name || 'Patient';
          onWarningRef.current?.(
            `Scheduled ride #${trip.id} (${patientName}) has pickup ${alert.label.toLowerCase()}! Please ensure ambulance arrangements immediately.`,
            '🚨 Approaching Scheduled Pickup'
          );
          playScheduledAlertChime();
        }
      }
    });

    if (count > 0 || tripsToCheck.some((t) => t.stage === 'Scheduled')) {
      setUrgentScheduledCount(count);
    }
  }, []);


  // Loads trips for a given stage with pagination support.
  // When isLoadMore is true, fetches next page and appends to trips list.
  // When silent is true (e.g. background polling), refreshes all currently loaded trips.
  const loadStage = useCallback(async (stage, { silent = false, isLoadMore = false, limitOverride, dateFilterOverride } = {}) => {
    latestStageRequestRef.current = stage;
    const currentLimit = limitOverride ?? pageSizeRef.current;
    const offset = isLoadMore ? tripsLengthRef.current : 0;
    const activeDateFilter = dateFilterOverride ?? dateFilterRef.current;

    if (isLoadMore) {
      setLoadingMore(true);
    } else if (!silent) {
      setLoading(true);
    }

    try {
      const result = await commandCenterService.listBookings({
        stage,
        limit: currentLimit,
        offset,
        startDate: activeDateFilter?.startDate || undefined,
        endDate: activeDateFilter?.endDate || undefined,
      });

      if (latestStageRequestRef.current !== stage) return; // superseded by a newer tab switch
      const bookings = result.data?.bookings || [];
      const newTrips = bookings.map((b) => mapBookingToTripUI(b, stage));
      const totalCount = result.data?.total ?? (offset + bookings.length);

      setTotalTrips(totalCount);
      setStageCounts((prev) => (prev[stage] === totalCount ? prev : { ...prev, [stage]: totalCount }));

      if (isLoadMore) {
        setTrips((prevTrips) => {
          const existingIds = new Set(prevTrips.map((t) => t.id));
          const uniqueNew = newTrips.filter((t) => !existingIds.has(t.id));
          return [...prevTrips, ...uniqueNew];
        });
      } else {
        setTrips((prevTrips) => {
          // If data hasn't changed, return prevTrips to avoid any re-render
          if (prevTrips.length === newTrips.length) {
            const isSame = prevTrips.every((pt, i) => {
              const nt = newTrips[i];
              return (
                pt.id === nt.id &&
                pt.stage === nt.stage &&
                pt.driver?.name === nt.driver?.name &&
                pt.driver?.phone === nt.driver?.phone &&
                pt.driverMotion === nt.driverMotion &&
                pt.arrivedAt === nt.arrivedAt &&
                pt.atDropoffAt === nt.atDropoffAt
              );
            });
            if (isSame) return prevTrips;
          }
          return newTrips;
        });
      }
    } catch (error) {
      if (latestStageRequestRef.current === stage && !silent) {
        onErrorRef.current?.(error.message || 'Failed to load bookings');
      }
    } finally {
      if (latestStageRequestRef.current === stage) {
        if (isLoadMore) setLoadingMore(false);
        else if (!silent) setLoading(false);
      }
    }
  }, []);

  // Fetches latest count badges for all pipeline stages in one shot
  const loadAllStageCounts = useCallback(async (dateFilterOverride) => {
    const activeDateFilter = dateFilterOverride ?? dateFilterRef.current;
    try {
      const result = await commandCenterService.getPipelineCounts({
        startDate: activeDateFilter?.startDate || undefined,
        endDate: activeDateFilter?.endDate || undefined,
      });
      if (result.data) {
        setStageCounts((prev) => {
          let hasDiff = false;
          for (const key of Object.keys(result.data)) {
            if (prev[key] !== result.data[key]) {
              hasDiff = true;
              break;
            }
          }
          return hasDiff ? { ...prev, ...result.data } : prev;
        });
      }
    } catch {
      // Fallback: fetch lightweight limit:1 for each stage individually
      PIPELINE_STAGES.forEach((stage) => {
        commandCenterService.listBookings({
          stage,
          limit: 1,
          startDate: activeDateFilter?.startDate || undefined,
          endDate: activeDateFilter?.endDate || undefined,
        })
          .then((res) => {
            const count = res.data?.total ?? 0;
            setStageCounts((prev) => (prev[stage] === count ? prev : { ...prev, [stage]: count }));
          })
          .catch(() => {});
      });
    }
  }, []);

  // Load the active stage's trips and all tab badge counts whenever tab or sub-filter changes
  useEffect(() => {
    const stageToLoad = activeStage === 'Active Trips' && activeTripFilter !== 'All' ? activeTripFilter : activeStage;
    loadStage(stageToLoad, { silent: false });
    loadAllStageCounts();
  }, [activeStage, activeTripFilter, loadStage, loadAllStageCounts]);

  const refreshActiveStage = useCallback(() => {
    const activeDateFilter = dateFilterRef.current;
    if (search.trim()) {
      const query = search.trim();
      setIsSearching(true);
      commandCenterService
        .listBookings({
          search: query,
          limit: pageSizeRef.current,
          offset: 0,
          startDate: activeDateFilter?.startDate || undefined,
          endDate: activeDateFilter?.endDate || undefined,
        })
        .then((result) => {
          const bookings = result.data?.bookings || [];
          setUniversalSearchResults(bookings.map((b) => mapBookingToTripUI(b, deriveTripStage(b))));
          setSearchTotal(result.data?.total ?? bookings.length);
        })
        .catch(() => {})
        .finally(() => setIsSearching(false));
      loadAllStageCounts();
    } else {
      const stageToRefresh = activeStage === 'Active Trips' && activeTripFilter !== 'All' ? activeTripFilter : activeStage;
      const countToRefresh = Math.max(pageSizeRef.current, tripsLengthRef.current);
      loadStage(stageToRefresh, { silent: false, limitOverride: countToRefresh });
      loadAllStageCounts();
    }
  }, [activeStage, activeTripFilter, loadStage, loadAllStageCounts, search]);

  const handleLoadMore = () => {
    if (loadingMore) return;
    const activeDateFilter = dateFilterRef.current;
    if (search.trim()) {
      const query = search.trim();
      const offset = universalSearchResults?.length || 0;
      setLoadingMore(true);
      commandCenterService
        .listBookings({
          search: query,
          limit: pageSize,
          offset,
          startDate: activeDateFilter?.startDate || undefined,
          endDate: activeDateFilter?.endDate || undefined,
        })
        .then((result) => {
          const bookings = result.data?.bookings || [];
          const mapped = bookings.map((b) => mapBookingToTripUI(b, deriveTripStage(b)));
          setUniversalSearchResults((prev) => {
            const existingIds = new Set((prev || []).map((t) => t.id));
            const unique = mapped.filter((t) => !existingIds.has(t.id));
            return [...(prev || []), ...unique];
          });
          if (result.data?.total != null) {
            setSearchTotal(result.data.total);
          }
        })
        .catch((err) => {
          onErrorRef.current?.(err.message || 'Failed to load more search results');
        })
        .finally(() => setLoadingMore(false));
    } else {
      const stageToLoad = activeStage === 'Active Trips' && activeTripFilter !== 'All' ? activeTripFilter : activeStage;
      loadStage(stageToLoad, { isLoadMore: true });
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    pageSizeRef.current = newSize;
    const activeDateFilter = dateFilterRef.current;
    if (search.trim()) {
      const query = search.trim();
      setIsSearching(true);
      commandCenterService
        .listBookings({
          search: query,
          limit: newSize,
          offset: 0,
          startDate: activeDateFilter?.startDate || undefined,
          endDate: activeDateFilter?.endDate || undefined,
        })
        .then((result) => {
          const bookings = result.data?.bookings || [];
          setUniversalSearchResults(bookings.map((b) => mapBookingToTripUI(b, deriveTripStage(b))));
          setSearchTotal(result.data?.total ?? bookings.length);
        })
        .catch(() => {})
        .finally(() => setIsSearching(false));
    } else {
      const stageToLoad = activeStage === 'Active Trips' && activeTripFilter !== 'All' ? activeTripFilter : activeStage;
      loadStage(stageToLoad, { silent: false, limitOverride: newSize });
    }
  };

  const handleDateFilterChange = (newDateFilter) => {
    setDateFilter(newDateFilter);
    dateFilterRef.current = newDateFilter;

    if (search.trim()) {
      const query = search.trim();
      setIsSearching(true);
      commandCenterService
        .listBookings({
          search: query,
          limit: pageSizeRef.current,
          offset: 0,
          startDate: newDateFilter?.startDate || undefined,
          endDate: newDateFilter?.endDate || undefined,
        })
        .then((result) => {
          const bookings = result.data?.bookings || [];
          setUniversalSearchResults(bookings.map((b) => mapBookingToTripUI(b, deriveTripStage(b))));
          setSearchTotal(result.data?.total ?? bookings.length);
        })
        .catch(() => {})
        .finally(() => setIsSearching(false));
    } else {
      const stageToLoad = activeStage === 'Active Trips' && activeTripFilter !== 'All' ? activeTripFilter : activeStage;
      loadStage(stageToLoad, { silent: false, dateFilterOverride: newDateFilter });
    }
    loadAllStageCounts(newDateFilter);
  };

  const isAnyModalOpen = Boolean(
    driverModal || cancelModal || deleteModal || viewingTrip || viewingMapTrip || infoTrip || stageModal
  );

  const activeStageRef = useRef(activeStage);
  activeStageRef.current = activeStage;

  const activeTripFilterRef = useRef(activeTripFilter);
  activeTripFilterRef.current = activeTripFilter;

  const isAnyModalOpenRef = useRef(isAnyModalOpen);
  isAnyModalOpenRef.current = isAnyModalOpen;

  const isFetchingRef = useRef(false);

  // Continuously evaluate loaded trips for scheduled rides approaching pickup within 1 hour
  useEffect(() => {
    checkApproachingScheduledRides(trips);
  }, [trips, checkApproachingScheduledRides]);

  // Approach 1 (Completely Silent Background Polling):
  // Polls active stage trips and all pipeline badge counts every 15 seconds silently.
  // Never flashes spinners or loading screens (silent = true).
  // Pauses when tab is hidden or any modal is open to ensure zero UI disruption.
  // Syncs immediately when user switches back to this browser tab.
  useEffect(() => {
    const pollIntervalMs = 15000;

    const poll = async () => {
      if (document.hidden || isAnyModalOpenRef.current || isFetchingRef.current) return;
      isFetchingRef.current = true;
      try {
        const stageToPoll = activeStageRef.current === 'Active Trips' && activeTripFilterRef.current !== 'All'
          ? activeTripFilterRef.current
          : activeStageRef.current;
        const countToKeep = Math.max(pageSizeRef.current, tripsLengthRef.current);
        const pollPromises = [
          loadStage(stageToPoll, { silent: true, limitOverride: countToKeep }),
          loadAllStageCounts(),
        ];

        // Also poll scheduled bookings in background so operators get alerted even while viewing other tabs
        if (stageToPoll !== 'Scheduled') {
          pollPromises.push(
            commandCenterService
              .listBookings({ stage: 'Scheduled', limit: 20 })
              .then((res) => {
                const scheduledBookings = res.data?.bookings || [];
                const mapped = scheduledBookings.map((b) => mapBookingToTripUI(b, 'Scheduled'));
                checkApproachingScheduledRides(mapped);
              })
              .catch(() => {})
          );
        }

        await Promise.all(pollPromises);
      } catch {
        // Background polling errors are silent
      } finally {
        isFetchingRef.current = false;
      }
    };

    const intervalId = setInterval(poll, pollIntervalMs);

    const handleVisibilityChange = () => {
      if (!document.hidden && !isAnyModalOpenRef.current) {
        poll();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadStage, loadAllStageCounts]);

  // Universal search effect: queries across ALL pipeline stages in backend whenever search query changes
  useEffect(() => {
    const query = search.trim();
    latestSearchQueryRef.current = query;

    if (!query) {
      setUniversalSearchResults(null);
      setSearchTotal(0);
      setIsSearching(false);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      return;
    }

    setIsSearching(true);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    searchDebounceRef.current = setTimeout(async () => {
      try {
        const activeDateFilter = dateFilterRef.current;
        const result = await commandCenterService.listBookings({
          search: query,
          limit: pageSizeRef.current,
          offset: 0,
          startDate: activeDateFilter?.startDate || undefined,
          endDate: activeDateFilter?.endDate || undefined,
        });

        if (latestSearchQueryRef.current !== query) return;

        const bookings = result.data?.bookings || [];
        const mapped = bookings.map((b) => mapBookingToTripUI(b, deriveTripStage(b)));
        setUniversalSearchResults(mapped);
        setSearchTotal(result.data?.total ?? bookings.length);
      } catch (err) {
        console.error('Universal search error:', err);
        if (latestSearchQueryRef.current === query) {
          setUniversalSearchResults([]);
          setSearchTotal(0);
        }
      } finally {
        if (latestSearchQueryRef.current === query) {
          setIsSearching(false);
        }
      }
    }, 250);

    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search]);

  const filteredTrips = useMemo(() => {
    let sourceList = universalSearchResults !== null ? universalSearchResults : trips;
    if (activeStage === 'Active Trips' && activeTripFilter !== 'All' && !search.trim()) {
      sourceList = sourceList.filter((trip) => trip.stage === activeTripFilter);
    }
    const rawQuery = search.trim().toLowerCase();
    let result = sourceList;
    if (rawQuery) {
      const cleanQuery = rawQuery.replace(/[\s\-_]/g, '');
      const tokens = rawQuery.split(/\s+/).filter(Boolean);

      result = sourceList.filter((trip) => {
        // 1. Trip ID
        const tripId = String(trip.id || '').toLowerCase();
        if (tripId.includes(rawQuery) || (cleanQuery && tripId.replace(/[\s\-_#]/g, '').includes(cleanQuery))) {
          return true;
        }

        // 2. Customer Name & Phone
        const customerName = String(trip.customer?.name || '').toLowerCase();
        const customerPhone = String(trip.customer?.phone || '').toLowerCase();
        if (customerName.includes(rawQuery)) return true;
        if (customerPhone.includes(rawQuery) || (cleanQuery && customerPhone.replace(/[\s\-_+()]/g, '').includes(cleanQuery))) {
          return true;
        }

        // 3. Driver Name & Phone
        const driverName = String(trip.driver?.name || '').toLowerCase();
        const driverPhone = String(trip.driver?.phone || '').toLowerCase();
        if (driverName && driverName.includes(rawQuery)) return true;
        if (driverPhone && (driverPhone.includes(rawQuery) || (cleanQuery && driverPhone.replace(/[\s\-_+()]/g, '').includes(cleanQuery)))) {
          return true;
        }

        // 4. Vehicle Number (Plate Number)
        const vehiclePlate = String(trip.vehicle?.plateNumber || trip.driver?.vehiclePlate || '').toLowerCase();
        if (vehiclePlate && vehiclePlate !== '—') {
          if (vehiclePlate.includes(rawQuery) || (cleanQuery && vehiclePlate.replace(/[\s\-_]/g, '').includes(cleanQuery))) {
            return true;
          }
        }

        // 5. Ambulance Type / Service
        const service = String(trip.service || '').toLowerCase();
        const serviceCategory = String(trip.serviceCategory || '').toLowerCase();
        const vehicleType = String(trip.vehicle?.type || trip.driver?.vehicleType || '').toLowerCase();
        if (service.includes(rawQuery) || serviceCategory.includes(rawQuery) || vehicleType.includes(rawQuery)) {
          return true;
        }

        if (Array.isArray(trip.requestedVehicleTypes)) {
          if (trip.requestedVehicleTypes.some((t) => String(t || '').toLowerCase().includes(rawQuery))) {
            return true;
          }
        }

        // 6. Hospital & Patient Condition
        const hospitalName = String(trip.hospital?.name || '').toLowerCase();
        if (hospitalName && hospitalName.includes(rawQuery)) return true;

        const condition = String(trip.condition || '').toLowerCase();
        if (condition && condition !== '—' && condition.includes(rawQuery)) {
          return true;
        }

        // 7. Multi-word token match (e.g. "Driver 5", "ALS 5028", "Dev Cardiac")
        // Builds a searchable corpus for the trip so typing words in any order matches
        if (tokens.length > 1) {
          const fullCorpus = [
            tripId,
            customerName,
            customerPhone,
            driverName,
            driverPhone,
            vehiclePlate !== '—' ? vehiclePlate : '',
            service,
            serviceCategory,
            vehicleType !== '—' ? vehicleType : '',
            hospitalName,
            condition !== '—' ? condition : '',
          ].join(' ');

          if (tokens.every((token) => fullCorpus.includes(token))) {
            return true;
          }
        }

        return false;
      });
    }

    // When a date range filter is active (e.g. August to October), display records in chronological order
    // starting with the earliest date and progressing to the latest date (August → September → October)
    if (dateFilter.period !== 'all') {
      return [...result].sort((a, b) => {
        const timeA = new Date(a.createdAt || a.when?.at || 0).getTime();
        const timeB = new Date(b.createdAt || b.when?.at || 0).getTime();
        return timeA - timeB;
      });
    }

    // Always prioritize urgent scheduled rides (pickup within 1h) at the TOP of the table
    if (activeStage === 'Scheduled') {
      return [...result].sort((a, b) => {
        const isUrgentA = Boolean(getScheduledPickupAlert(a)?.isUrgent);
        const isUrgentB = Boolean(getScheduledPickupAlert(b)?.isUrgent);

        if (isUrgentA && !isUrgentB) return -1;
        if (!isUrgentA && isUrgentB) return 1;

        if (isUrgentA && isUrgentB) {
          const timeA = new Date(a.scheduledDatetime || a.when?.at || 0).getTime();
          const timeB = new Date(b.scheduledDatetime || b.when?.at || 0).getTime();
          return timeA - timeB;
        }

        return 0;
      });
    }

    return result;
  }, [trips, search, universalSearchResults, activeStage, activeTripFilter, dateFilter]);

  const handleAction = (message) => {
    if (onAction) onAction(message);
  };

  const handleCancelBooking = async () => {
    if (!cancelModal || !cancelReason.trim() || cancelling) return;
    setCancelling(true);
    try {
      await commandCenterService.cancelBooking(cancelModal.trip.id, { reason: cancelReason.trim() });
      handleAction(`Cancelled booking #${cancelModal.trip.id} — ${cancelReason.trim()}`);
      setCancelModal(null);
      setCancelReason('');
      refreshActiveStage();
    } catch (error) {
      onError?.(error.message || 'Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  const handleDeleteBooking = async () => {
    if (!deleteModal || deleting) return;
    setDeleting(true);
    try {
      await commandCenterService.deleteBooking(deleteModal.trip.id);
      handleAction(`Deleted booking #${deleteModal.trip.id}`);
      setDeleteModal(null);
      refreshActiveStage();
    } catch (error) {
      onError?.(error.message || 'Failed to delete booking');
    } finally {
      setDeleting(false);
    }
  };

  const activeTripsTotal = stageCounts['Active Trips'] ?? (
    (stageCounts['Towards Pickup'] || 0) +
    (stageCounts['At Pickup'] || 0) +
    (stageCounts['Towards Drop'] || 0) +
    (stageCounts['At Drop'] || 0) +
    (stageCounts['Untracked'] || 0)
  );

  const totalRidesCount = stageCounts.total ?? stageCounts['Total Rides'] ?? (
    (stageCounts['Requested'] || 0) +
    (stageCounts['Pending'] || 0) +
    (stageCounts['Scheduled'] || 0) +
    activeTripsTotal +
    (stageCounts['Completed'] || 0) +
    (stageCounts['Cancelled'] || 0)
  );

  const effectiveTotal = search.trim()
    ? searchTotal
    : (activeStage === 'Active Trips'
        ? (activeTripFilter === 'All' ? activeTripsTotal : (stageCounts[activeTripFilter] ?? totalTrips))
        : (totalTrips || (stageCounts[activeStage] ?? 0)));

  const hasMoreTrips = filteredTrips.length < effectiveTotal;

  return (
    <div className="space-y-4">
      {/* Header - temporarily commented out as requested */}
      {/* 
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Booking Pipeline</h2>
          <p className="text-xs text-gray-500 mt-0.5">Live bookings from the dispatch backend.</p>
        </div>
        <button
          onClick={onCreateBooking}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Create Booking
        </button>
      </div>
      */}

      {/* Pipeline stepper */}
      <div className="bg-white rounded-2xl shadow p-5">
        <div className="flex items-center gap-2.5 mb-3">
          <span className="text-[11px] font-semibold tracking-wide text-gray-400 uppercase">PIPELINE STAGE</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            Total Rides: <span className="font-semibold text-gray-900 ml-1">{totalRidesCount}</span>
          </span>
        </div>

        <div className="flex items-center overflow-x-auto pb-1">
          {PIPELINE_STAGE_GROUPS.main.map((stage, index) => {
            const isActive = stage === activeStage;
            const count = stage === 'Active Trips'
              ? activeTripsTotal
              : (stageCounts[stage] ?? 0);

            const style = PIPELINE_STAGE_STYLE[stage] || {
              labelColor: 'text-gray-700',
              countColor: 'text-gray-500',
              defaultBg: 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50',
              activeBg: 'bg-primary/10 text-primary border-primary/20',
            };

            return (
              <React.Fragment key={stage}>
                {index > 0 && (
                  <div className="w-4 sm:w-6 border-t border-gray-200 mx-1 shrink-0" />
                )}
                <button
                  onClick={() => {
                    if (stage !== activeStage || search) {
                      setLoading(true);
                      setActiveStage(stage);
                      if (stage !== 'Active Trips') setActiveTripFilter('All');
                      if (search) {
                        setSearch('');
                        setUniversalSearchResults(null);
                      }
                    }
                  }}
                  className={`shrink-0 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border outline-none focus:outline-none focus:ring-0 select-none transition-colors cursor-pointer ${
                    isActive && !search.trim()
                      ? style.activeBg
                      : style.defaultBg
                  }`}
                >
                  {renderPipelineStageIcon(stage, isActive && !search.trim())}
                  <span className={isActive && !search.trim() ? 'font-semibold' : ''}>{stage}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                    isActive && !search.trim() ? 'bg-white/25 text-white font-bold' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {count}
                  </span>
                  {stage === 'Scheduled' && urgentScheduledCount > 0 && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white animate-pulse shadow-xs shrink-0"
                      title={`${urgentScheduledCount} scheduled ride(s) approaching pickup within 1 hour!`}
                    >
                      <AlertTriangle className="w-2.5 h-2.5" />
                      {urgentScheduledCount} urgent
                    </span>
                  )}
                </button>
              </React.Fragment>
            );
          })}

          {/* Divider separating Main Flow from Exception Stages */}
          <div className="h-6 w-px bg-gray-200 mx-2 sm:mx-3 shrink-0" />

          {/* Exception Stages: Cancelled & Rejected displayed as consistent pipeline status pills */}
          <div className="flex items-center gap-2 shrink-0">
            {PIPELINE_STAGE_GROUPS.exceptions.map((stage) => {
              const isActive = stage === activeStage;
              const count = stageCounts[stage] ?? 0;
              const style = PIPELINE_STAGE_STYLE[stage] || {
                defaultBg: 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50',
                activeBg: 'bg-red-600 text-white border-red-600 shadow-sm font-semibold',
                badgeDefault: 'bg-gray-100 text-gray-600',
                badgeActive: 'bg-white/25 text-white font-bold',
              };

              return (
                <button
                  key={stage}
                  onClick={() => {
                    if (stage !== activeStage || search) {
                      setLoading(true);
                      setActiveStage(stage);
                      setActiveTripFilter('All');
                      if (search) {
                        setSearch('');
                        setUniversalSearchResults(null);
                      }
                    }
                  }}
                  className={`shrink-0 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border outline-none focus:outline-none focus:ring-0 select-none transition-colors cursor-pointer ${
                    isActive && !search.trim() ? style.activeBg : style.defaultBg
                  }`}
                >
                  {renderPipelineStageIcon(stage, isActive && !search.trim())}
                  <span className={isActive && !search.trim() ? 'font-semibold' : ''}>{stage}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                    isActive && !search.trim() ? style.badgeActive : style.badgeDefault
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Search + filter row */}
      <div className="bg-white rounded-2xl shadow p-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="flex-1 max-w-md">
          <div className="text-[11px] font-semibold tracking-wide text-gray-400 mb-2 flex items-center justify-between">
            <span>FIND A TRIP</span>
            {search.trim() && (
              <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">
                Searching All Pipelines
              </span>
            )}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                const val = e.target.value;
                setSearch(val);
                if (val.trim()) {
                  setIsSearching(true);
                }
              }}
              placeholder="Search by trip ID, patient, driver, phone, vehicle plate, type…"
              className="w-full pl-9 pr-9 py-2 rounded-full border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            {isSearching ? (
              <Loader2 className="w-3.5 h-3.5 text-blue-600 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />
            ) : search ? (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setUniversalSearchResults(null);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                title="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            ) : null}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {search.trim()
              ? 'Universal search across all pipeline stages (Pending, Towards Drop, Completed, etc.).'
              : 'Universal search across all pipeline stages as you type.'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Date range filter */}
          <CommandCenterDateFilter
            value={dateFilter}
            onChange={handleDateFilterChange}
          />

          {/* Page size limit filter */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-white text-xs font-medium text-gray-700 shadow-2xs hover:border-gray-300">
            <span className="text-gray-400">Show:</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="bg-transparent font-semibold text-gray-900 outline-none cursor-pointer pr-1"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <button
            onClick={refreshActiveStage}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-medium"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading || isSearching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Active Trips Sub-status Filters */}
      {activeStage === 'Active Trips' && !search.trim() && (
        <div className="bg-white rounded-2xl shadow p-4 border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
              <Navigation className="w-3.5 h-3.5 text-indigo-600 fill-indigo-600" />
              <span>Active Trip Stages</span>
            </div>
            <span className="text-xs text-gray-400">
              Showing {filteredTrips.length} of {activeTripsTotal} active trips
            </span>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {/* All Active Filter */}
            <button
              onClick={() => setActiveTripFilter('All')}
              className={`shrink-0 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold outline-none focus:outline-none focus:ring-0 select-none border ${
                activeTripFilter === 'All'
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <span>All Active</span>
              <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-bold ${
                activeTripFilter === 'All' ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-700'
              }`}>
                {activeTripsTotal}
              </span>
            </button>

            {/* 5 Sub-stage Filters */}
            {ACTIVE_TRIP_SUBSTAGES.map((subStage) => {
              const isSelected = activeTripFilter === subStage;
              const count = stageCounts[subStage] ?? 0;
              const config = SUBSTAGE_FILTER_CONFIG[subStage];
              const Icon = config.icon;
              return (
                <button
                  key={subStage}
                  onClick={() => setActiveTripFilter(subStage)}
                  className={`shrink-0 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold outline-none focus:outline-none focus:ring-0 select-none border ${
                    isSelected
                      ? config.activeClass
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className={`w-3.5 h-3.5 ${isSelected ? config.iconActiveClass : config.iconClass}`} />
                  <span>{subStage}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-bold ${
                    isSelected ? config.badgeActiveClass : 'bg-gray-100 text-gray-600'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow overflow-hidden">
        <div className="px-5 py-3 text-sm text-gray-500 border-b border-gray-100 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {search.trim() ? (
              isSearching ? (
                <span className="flex items-center gap-2 text-gray-600">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600 inline" />
                  Searching across all pipelines…
                </span>
              ) : (
                <span>
                  Found <span className="font-semibold text-gray-900">{filteredTrips.length}</span> matching trip{filteredTrips.length === 1 ? '' : 's'} across all pipelines for{' '}
                  <span className="font-semibold text-blue-600">"{search.trim()}"</span>
                </span>
              )
            ) : loading ? (
              <span className="flex items-center gap-2 text-gray-600">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600 inline" />
                Loading trips…
              </span>
            ) : (
              <span>
                Showing <span className="font-semibold text-gray-900">{filteredTrips.length}</span> of{' '}
                <span className="font-semibold text-gray-900">{effectiveTotal}</span> trips in{' '}
                <span className="font-semibold text-gray-900">{activeStage === 'Active Trips' && activeTripFilter !== 'All' ? activeTripFilter : activeStage}</span>
              </span>
            )}

            {dateFilter.period !== 'all' && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                <SlidersHorizontal className="w-3 h-3 text-blue-600 shrink-0" />
                <span>Period: {dateFilter.label}</span>
                <button
                  type="button"
                  onClick={() => handleDateFilterChange({ period: 'all', startDate: null, endDate: null, label: 'All Time' })}
                  className="hover:text-blue-900 cursor-pointer p-0.5 rounded-full hover:bg-blue-100 transition-colors ml-0.5"
                  title="Clear date filter"
                >
                  <X className="w-3 h-3 stroke-[2.5]" />
                </button>
              </span>
            )}
          </div>
          {search.trim() && (
            <button
              onClick={() => {
                setSearch('');
                setUniversalSearchResults(null);
              }}
              className="text-xs text-blue-600 hover:text-blue-800 font-semibold cursor-pointer hover:underline"
            >
              Clear Search &amp; Return to {activeStage}
            </button>
          )}
        </div>

        {isSearching ? (
          <div className="px-5 py-12 flex flex-col items-center justify-center gap-2.5 text-sm text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            <span>Searching across all pipeline stages…</span>
          </div>
        ) : loading && !search.trim() ? (
          <div className="px-5 py-12 flex flex-col items-center justify-center gap-2.5 text-sm text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            <span>Loading trips…</span>
          </div>
        ) : filteredTrips.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-gray-400">
            {search.trim() ? (
              <div className="space-y-1">
                <p className="font-medium text-gray-700">No trips found matching "{search.trim()}"</p>
                <p className="text-xs text-gray-400">
                  {dateFilter.period !== 'all'
                    ? `Checked across all pipeline stages within "${dateFilter.label}".`
                    : 'Checked across all pipeline stages (Pending, Scheduled, Towards Pickup, Towards Drop, Completed, etc.).'}
                </p>
                {dateFilter.period !== 'all' && (
                  <button
                    type="button"
                    onClick={() => handleDateFilterChange({ period: 'all', startDate: null, endDate: null, label: 'All Time' })}
                    className="mt-2 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                  >
                    Clear Date Filter &amp; Search All Time
                  </button>
                )}
              </div>
            ) : dateFilter.period !== 'all' ? (
              <div className="space-y-1.5">
                <p className="font-medium text-gray-700">No records found for {dateFilter.label}</p>
                <p className="text-xs text-gray-400">
                  There are no trips in <span className="font-semibold text-gray-600">{activeStage}</span> within the selected time period.
                </p>
                <button
                  type="button"
                  onClick={() => handleDateFilterChange({ period: 'all', startDate: null, endDate: null, label: 'All Time' })}
                  className="mt-2 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                >
                  Clear Date Filter &amp; Show All Records
                </button>
              </div>
            ) : (
              'No trips found for this stage.'
            )}
          </div>
        ) : (
          <>
          {/* Mobile: one stacked card per trip instead of a wide table */}
          <div className="sm:hidden divide-y divide-gray-100">
            {filteredTrips.map((trip) => {
              const motion = trip.driverMotion ? MOTION_BADGE[trip.driverMotion] : null;
              const scheduledAlert = getScheduledPickupAlert(trip);
              return (
                <div
                  key={trip.id}
                  className="p-4 space-y-2.5 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`inline-flex items-center gap-1 font-mono text-xs font-bold px-2 py-1 rounded-md border ${
                        scheduledAlert
                          ? 'bg-red-50 text-red-600 border-red-300 ring-2 ring-red-500/20'
                          : 'bg-slate-100 text-slate-800 border-slate-200'
                      }`}
                    >
                      {scheduledAlert && <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse shrink-0" />}
                      #{trip.id}
                    </span>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${STATUS_BADGE_CLASS[trip.stage]}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />
                      {trip.stage}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    {(() => {
                      const stageTimer = getActiveStageTimerConfig(trip);
                      if (!stageTimer) return null;
                      return (
                        <div className="flex items-center gap-1 text-gray-600">
                          <span className="text-[11px] text-gray-400">{stageTimer.label}</span>
                          <SlaTimer
                            startAt={stageTimer.startAt}
                            warnAmberSeconds={stageTimer.warnAmberSeconds}
                            warnRedSeconds={stageTimer.warnRedSeconds}
                            label="Stage SLA"
                          />
                        </div>
                      );
                    })()}
                    {['Towards Pickup', 'At Pickup', 'Towards Drop', 'At Drop', 'Untracked'].includes(trip.stage) && getTripStartTime(trip) && (
                      <div className="flex items-center gap-1 text-gray-600">
                        <span className="text-[11px] text-gray-400 font-medium">Trip SLA:</span>
                        <SlaTimer
                          startAt={getTripStartTime(trip)}
                          warnAmberSeconds={45 * 60}
                          warnRedSeconds={60 * 60}
                          label="Trip SLA"
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="font-medium text-gray-900">{trip.customer.name}</div>
                    {trip.customer?.phone && trip.customer.phone !== '—' ? (
                      <CopyablePhone
                        phone={trip.customer.phone}
                        callLabel="Call patient"
                        showIcon={true}
                        iconSize="w-3 h-3"
                        textColor="text-gray-500 hover:text-blue-600"
                        className="mt-0.5"
                        callClassName="text-xs"
                      />
                    ) : (
                      <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                        <Phone className="w-3 h-3" />
                        <span>—</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm text-gray-700 font-medium">{/* {trip.serviceCategory} &middot; */}{trip.service}</div>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0 ${PRIORITY_BADGE_CLASS[trip.priority]}`}>
                      {trip.priority === 'EMERGENCY' ? 'EMERGENCY' : 'NON-EMERGENCY'}
                    </span>
                  </div>

                  <div className="text-xs text-gray-500">{trip.condition}</div>

                  <div className="flex items-center gap-1.5 text-xs text-gray-600">
                    <Calendar className="w-3.5 h-3.5 text-primary/70 shrink-0" />
                    <span className="uppercase tracking-wide text-gray-400">{trip.when.label}:</span>
                    <span>{formatWhen(trip.when.at)}</span>
                  </div>

                  {trip.driver ? (
                    <div className="flex flex-col gap-1.5 pt-1">
                      {motion && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold w-fit ${motion.className}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            trip.driverMotion === 'live' ? 'bg-green-500 animate-pulse'
                              : trip.driverMotion === 'untracked' ? 'bg-gray-400'
                              : 'bg-amber-500'
                          }`} />
                          {motion.label}
                        </span>
                      )}
                      <div>
                        <div className="font-medium text-gray-900 text-xs">{trip.driver.name}</div>
                        {trip.driver.phone && (
                          <CopyablePhone
                            phone={trip.driver.phone}
                            callLabel="Call driver"
                            showIcon={true}
                            iconSize="w-3 h-3"
                            textColor="text-blue-600 hover:text-blue-800"
                            className="mt-0.5"
                            callClassName="text-xs font-medium"
                          />
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <button
                          onClick={() => setViewingTrip(trip)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium"
                        >
                          <Eye className="w-3 h-3" />
                          View
                        </button>
                        {trip.stage === 'Scheduled' && (
                          <button
                            onClick={() => setDriverModal({ trip, mode: 'change' })}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-medium"
                          >
                            <RefreshCw className="w-3 h-3" />
                            Change
                          </button>
                        )}
                      </div>
                    </div>
                  ) : !TERMINAL_STAGES.includes(trip.stage) ? (
                    <div className="flex items-center gap-1.5 flex-wrap pt-1">
                      <button
                        onClick={() => setDriverModal({ trip, mode: 'assign' })}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium"
                      >
                        <UserPlus className="w-3 h-3" />
                        Assign
                      </button>
                    </div>
                  ) : null}

                  <div className="flex flex-col gap-2 pt-1">
                    {/* Stage Status Action Button (mobile) */}
                    {!TERMINAL_STAGES.includes(trip.stage) && (() => {
                      const action = getNextStageAction(trip);
                      if (!action) return null;
                      return (
                        <div className="w-full flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => {
                              const allowed = VALID_NEXT_STAGES[trip.stage] || [action.target];
                              setStageModal({
                                trip,
                                currentStage: trip.stage,
                                targetStage: action.target,
                                allowedTargets: allowed,
                                action,
                              });
                              setStageModalNotes('');
                            }}
                            title={`Click to advance: ${action.label}`}
                            className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors shadow-xs active:scale-[0.99] ${action.badgeClass || 'border-purple-300 bg-purple-50/80 text-purple-700 hover:bg-purple-100/80 hover:border-purple-400'}`}
                          >
                            <span className="w-2 h-2 rounded-full bg-current shrink-0" />
                            <span>{action.label}</span>
                          </button>
                        </div>
                      );
                    })()}

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setViewingMapTrip(trip)}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-medium"
                      >
                        <MapPin className="w-3 h-3" />
                        Map
                      </button>
                      {TERMINAL_STAGES.includes(trip.stage) ? (
                        <span className={`flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${ACTIONS_BADGE_CLASS[trip.stage]}`}>
                          {ACTIONS_BADGE_LABEL[trip.stage]}
                        </span>
                      ) : (
                        <>
                          {!['Completed', 'Cancelled'].includes(trip.stage) && (
                            <button
                              type="button"
                              onClick={() => onCreateBooking?.(trip.id)}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-blue-200 text-blue-600 bg-white hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors shadow-2xs cursor-pointer"
                              title="Edit"
                              aria-label="Edit"
                            >
                              <Pencil className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                            </button>
                          )}
                          {CANCELLABLE_STAGES.includes(trip.stage) && (
                            <button
                              type="button"
                              onClick={() => setCancelModal({ trip })}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-red-200 text-red-600 bg-white hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-colors shadow-2xs cursor-pointer"
                              title="Cancel"
                              aria-label="Cancel"
                            >
                              <Ban className="w-3.5 h-3.5 text-red-500 shrink-0" />
                            </button>
                          )}
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          if (trip.stage === 'Completed') {
                            setViewingMapTrip(trip);
                          } else {
                            setInfoTrip(trip);
                          }
                        }}
                        className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900 transition-colors shadow-2xs cursor-pointer"
                        title={trip.stage === 'Completed' ? 'Details (Map & Route History)' : 'Details'}
                        aria-label="Details"
                      >
                        <Eye className="w-3.5 h-3.5 text-gray-600 shrink-0" />
                      </button>
                      {/* Delete button commented out to prevent accidental deletion of important operational records */}
                      {/*
                      <button
                        type="button"
                        onClick={() => setDeleteModal({ trip })}
                        title="Delete"
                        aria-label="Delete"
                        className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-red-200 text-red-600 bg-white hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-colors shadow-2xs cursor-pointer"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-500 shrink-0" />
                      </button>
                      */}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tablet/desktop: full table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-primary/5 text-left text-[11px] tracking-wide text-gray-500">
                  <th className="px-5 py-2.5 font-semibold">TRIP</th>
                  <th className="px-5 py-2.5 font-semibold">PATIENT</th>
                  <th className="px-5 py-2.5 font-semibold">STATUS</th>
                  {/* <th className="px-5 py-2.5 font-semibold">DRIVER</th> */}
                  <th className="px-5 py-2.5 font-semibold">{/* SERVICE &amp; */}TYPE</th>
                  {/* <th className="px-5 py-2.5 font-semibold">CONDITION</th> */}
                  <th className="px-5 py-2.5 font-semibold">INITIATED AT</th>
                  {/* <th className="px-5 py-2.5 font-semibold">MAP</th> */}
                  <th className="px-5 py-2.5 font-semibold">STAGE ACTION</th>
                  <th className="px-5 py-2.5 font-semibold">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredTrips.map((trip) => {
                  const motion = trip.driverMotion ? MOTION_BADGE[trip.driverMotion] : null;
                  const scheduledAlert = getScheduledPickupAlert(trip);
                  return (
                    <tr
                      key={trip.id}
                      className="border-t border-gray-100 hover:bg-gray-50/60 transition-colors"
                    >
                      <td className="px-5 py-3 whitespace-nowrap">
                        <div className="flex flex-col gap-1.5 items-start">
                          <button
                            type="button"
                            onClick={() => {
                              if (trip.stage === 'Completed') {
                                setViewingMapTrip(trip);
                              } else {
                                setInfoTrip(trip);
                              }
                            }}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold font-mono transition-colors cursor-pointer w-fit ${
                              scheduledAlert
                                ? 'bg-red-50 text-red-600 border border-red-300 hover:bg-red-100 ring-2 ring-red-500/20'
                                : 'bg-primary/10 hover:bg-primary/20 text-primary'
                            }`}
                            title={
                              scheduledAlert
                                ? `Urgent Scheduled: ${scheduledAlert.label} (Pickup within 1 hour) - View Details`
                                : trip.stage === 'Completed'
                                ? 'View Trip Map & Route History'
                                : 'View Trip Details'
                            }
                          >
                            {scheduledAlert && <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse shrink-0" />}
                            #{trip.id}
                          </button>
                          {trip.driver ? (
                            <div className="flex flex-col gap-0.5 mt-0.5">
                              <div className="flex items-center gap-1.5 font-semibold text-gray-900 text-xs truncate max-w-[150px]" title={trip.driver.name}>
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                                <span className="truncate">{trip.driver.name}</span>
                              </div>
                              {trip.driver.phone && (
                                <CopyablePhone
                                  phone={trip.driver.phone}
                                  callLabel="Call driver"
                                  showIcon={true}
                                  iconSize="w-2.5 h-2.5"
                                  textColor="text-blue-600 hover:text-blue-800"
                                  className="ml-3 mt-0.5"
                                  callClassName="text-[11px] font-medium"
                                />
                              )}
                            </div>
                          ) : !TERMINAL_STAGES.includes(trip.stage) ? (
                            <button
                              type="button"
                              onClick={() => setDriverModal({ trip, mode: 'assign' })}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-semibold whitespace-nowrap shadow-xs hover:shadow-sm active:scale-[0.98] transition-all cursor-pointer group mt-0.5"
                              title="Assign Driver"
                            >
                              <UserPlus className="w-3.5 h-3.5 shrink-0 transition-transform group-hover:scale-110" />
                              <span>Assign Driver</span>
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400 italic mt-0.5">No driver</span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="font-medium text-gray-900">{trip.customer.name}</div>
                        {trip.customer?.phone && trip.customer.phone !== '—' ? (
                          <CopyablePhone
                            phone={trip.customer.phone}
                            callLabel="Call patient"
                            showIcon={true}
                            iconSize="w-3 h-3"
                            textColor="text-gray-500 hover:text-blue-600"
                            className="mt-0.5"
                            callClassName="text-xs"
                          />
                        ) : (
                          <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                            <Phone className="w-3 h-3" />
                            <span>—</span>
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-col gap-1.5">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold uppercase w-fit ${STATUS_BADGE_CLASS[trip.stage]}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />
                            {trip.stage}
                          </span>
                          {/* Stage-specific SLA timer (Towards Pickup, Wait at Pickup, Towards Drop, Wait at Drop) */}
                          {(() => {
                            const stageTimer = getActiveStageTimerConfig(trip);
                            if (!stageTimer) return null;
                            return (
                              <div className="flex items-center gap-1.5 text-[11px]">
                                <span className="text-gray-500 font-medium">Stage:</span>
                                <SlaTimer
                                  startAt={stageTimer.startAt}
                                  warnAmberSeconds={stageTimer.warnAmberSeconds}
                                  warnRedSeconds={stageTimer.warnRedSeconds}
                                  label="Stage SLA"
                                />
                              </div>
                            );
                          })()}
                          {/* Total Trip SLA Timer for Active Stages */}
                          {['Towards Pickup', 'At Pickup', 'Towards Drop', 'At Drop', 'Untracked'].includes(trip.stage) && getTripStartTime(trip) && (
                            <div className="flex items-center gap-1.5 text-[11px]">
                              <span className="text-gray-500 font-medium">Trip SLA:</span>
                              <SlaTimer
                                startAt={getTripStartTime(trip)}
                                warnAmberSeconds={45 * 60}
                                warnRedSeconds={60 * 60}
                                label="Trip SLA"
                              />
                            </div>
                          )}
                          {/* Completed Stage: Total Duration */}
                          {trip.stage === 'Completed' && (
                            <div className="flex items-center gap-1 text-[11px]">
                              <span className="text-gray-500 font-medium">Duration:</span>
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium tabular-nums ${
                                  (trip.actualDurationMinutes || 0) >= 60
                                    ? 'bg-red-50 text-red-600 border border-red-200 font-semibold'
                                    : (trip.actualDurationMinutes || 0) >= 45
                                    ? 'bg-amber-50 text-amber-700 border border-amber-300 font-medium'
                                    : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                }`}
                                title={`Total Trip Duration: ${formatDuration(trip.actualDurationMinutes)}`}
                              >
                                <Clock className="w-3 h-3" />
                                {formatDuration(trip.actualDurationMinutes)}
                              </span>
                            </div>
                          )}
                          {/* Scheduled Stage: If not yet started, show when it will start */}
                          {trip.stage === 'Scheduled' && (
                            <span className="text-[11px] text-slate-500">
                              Scheduled for {trip.scheduledDatetime ? new Date(trip.scheduledDatetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                            </span>
                          )}
                        </div>
                      </td>
                      {/* DRIVER column commented out */}
                      {/* <td className="px-5 py-3">
                        {trip.driver ? (
                          <div className="flex flex-col gap-1.5">
                            {motion && (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold w-fit ${motion.className}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${
                                  trip.driverMotion === 'live' ? 'bg-green-500 animate-pulse'
                                    : trip.driverMotion === 'untracked' ? 'bg-gray-400'
                                    : 'bg-amber-500'
                                }`} />
                                {motion.label}
                              </span>
                            )}
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => setViewingTrip(trip)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium cursor-pointer"
                              >
                                <Eye className="w-3 h-3" />
                                View
                              </button>
                              {trip.stage === 'Scheduled' && (
                                <button
                                  onClick={() => setDriverModal({ trip, mode: 'change' })}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-medium"
                                >
                                  <RefreshCw className="w-3 h-3" />
                                  Change
                                </button>
                              )}
                            </div>
                          </div>
                        ) : !TERMINAL_STAGES.includes(trip.stage) ? (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => setDriverModal({ trip, mode: 'assign' })}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium"
                            >
                              <UserPlus className="w-3 h-3" />
                              Assign
                            </button>
                          </div>
                        ) : null}
                      </td> */}
                      <td className="px-5 py-3">
                        <div className="text-gray-700 font-medium">{/* {trip.serviceCategory} &middot; */}{trip.service}</div>
                        <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${PRIORITY_BADGE_CLASS[trip.priority]}`}>
                          {trip.priority === 'EMERGENCY' ? 'EMERGENCY' : 'NON-EMERGENCY'}
                        </span>
                      </td>
                      {/* <td className="px-5 py-3 text-gray-500">{trip.condition}</td> */}
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <Calendar className="w-3.5 h-3.5 text-primary/70" />
                          <div>
                            <div className="text-[10px] uppercase tracking-wide text-gray-400">{trip.when.label}</div>
                            <div>{formatWhen(trip.when.at)}</div>
                          </div>
                        </div>
                      </td>
                      {/* <td className="px-5 py-3">
                        <button
                          onClick={() => setViewingMapTrip(trip)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-medium"
                        >
                          <MapPin className="w-3 h-3" />
                          Map
                        </button>
                      </td> */}
                      {/* STAGE ACTION COLUMN */}
                      <td className="px-5 py-3 whitespace-nowrap">
                        {TERMINAL_STAGES.includes(trip.stage) ? (
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${ACTIONS_BADGE_CLASS[trip.stage]}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />
                            {ACTIONS_BADGE_LABEL[trip.stage]}
                          </span>
                        ) : (() => {
                          const action = getNextStageAction(trip);
                          if (!action) {
                            return (
                              <button
                                type="button"
                                onClick={() => setDriverModal({ trip, mode: 'assign' })}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-dashed border-amber-300 bg-amber-50/70 hover:bg-amber-100/80 text-amber-800 text-xs font-semibold whitespace-nowrap cursor-pointer transition-all shadow-2xs hover:shadow-xs group"
                                title="Click to assign driver first"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse shrink-0" />
                                <span>Awaiting Driver</span>
                                <ArrowRight className="w-3 h-3 text-amber-600/70 group-hover:translate-x-0.5 transition-transform shrink-0" />
                              </button>
                            );
                          }
                          return (
                            <button
                              type="button"
                              onClick={() => {
                                const allowed = VALID_NEXT_STAGES[trip.stage] || [action.target];
                                setStageModal({
                                  trip,
                                  currentStage: trip.stage,
                                  targetStage: action.target,
                                  allowedTargets: allowed,
                                  action,
                                });
                                setStageModalNotes('');
                              }}
                              title={`Click to advance: ${action.label}`}
                              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-xs font-semibold whitespace-nowrap cursor-pointer transition-all shadow-2xs hover:shadow-xs active:scale-[0.98] ${
                                action.badgeClass || 'border-purple-300 bg-purple-50/80 text-purple-700 hover:bg-purple-100/80 hover:border-purple-400'
                              }`}
                            >
                              <span className="w-2 h-2 rounded-full bg-current shrink-0 animate-pulse" />
                              <span>{action.label}</span>
                              <ArrowRight className="w-3.5 h-3.5 opacity-60 ml-0.5 shrink-0" />
                            </button>
                          );
                        })()}
                      </td>

                      {/* ACTIONS COLUMN (Icon-Only Row with Tooltips) */}
                      <td className="px-5 py-3 whitespace-nowrap">
                        {TERMINAL_STAGES.includes(trip.stage) ? (
                          <div className="flex items-center gap-1.5">
                            {/* Details: Eye Icon Only */}
                            <button
                              type="button"
                              onClick={() => {
                                if (trip.stage === 'Completed') {
                                  setViewingMapTrip(trip);
                                } else {
                                  setInfoTrip(trip);
                                }
                              }}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900 transition-colors shadow-2xs cursor-pointer"
                              title={trip.stage === 'Completed' ? 'Details (Map & Route History)' : 'Details'}
                              aria-label="Details"
                            >
                              <Eye className="w-3.5 h-3.5 text-gray-600 shrink-0" />
                            </button>

                            {/* Delete button commented out to prevent accidental deletion of important operational records */}
                            {/*
                            <button
                              type="button"
                              onClick={() => setDeleteModal({ trip })}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-red-200 text-red-600 bg-white hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-colors shadow-2xs cursor-pointer"
                              title="Delete"
                              aria-label="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-500 shrink-0" />
                            </button>
                            */}
                          </div>
                        ) : (() => {
                          const isCancellable = CANCELLABLE_STAGES.includes(trip.stage);
                          return (
                            <div className="flex items-center gap-1.5">
                              {/* Details: Eye Icon Only */}
                              <button
                                type="button"
                                onClick={() => setInfoTrip(trip)}
                                className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900 transition-colors shadow-2xs cursor-pointer"
                                title="Details"
                                aria-label="Details"
                              >
                                <Eye className="w-3.5 h-3.5 text-gray-600 shrink-0" />
                              </button>

                              {/* Edit: Pencil Icon Only */}
                              {!['Completed', 'Cancelled'].includes(trip.stage) && (
                                <button
                                  type="button"
                                  onClick={() => onCreateBooking?.(trip.id)}
                                  className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-blue-200 text-blue-600 bg-white hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors shadow-2xs cursor-pointer"
                                  title="Edit"
                                  aria-label="Edit"
                                >
                                  <Pencil className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                                </button>
                              )}

                              {/* Cancel: Cancel/Ban Icon Only */}
                              <button
                                type="button"
                                onClick={() => isCancellable && setCancelModal({ trip })}
                                disabled={!isCancellable}
                                className={`h-8 w-8 inline-flex items-center justify-center rounded-lg border transition-colors shadow-2xs ${
                                  isCancellable
                                    ? 'border-red-200 text-red-600 bg-white hover:bg-red-50 hover:border-red-300 hover:text-red-700 cursor-pointer'
                                    : 'border-gray-200 text-gray-300 bg-gray-50/60 cursor-not-allowed select-none'
                                }`}
                                title={isCancellable ? 'Cancel' : 'Cancel (Cannot cancel trip at this stage)'}
                                aria-label="Cancel"
                              >
                                <Ban className={`w-3.5 h-3.5 shrink-0 ${isCancellable ? 'text-red-500' : 'text-gray-300'}`} />
                              </button>

                              {/* Delete button commented out to prevent accidental deletion of important operational records */}
                              {/*
                              <button
                                type="button"
                                onClick={() => setDeleteModal({ trip })}
                                className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-red-200 text-red-600 bg-white hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-colors shadow-2xs cursor-pointer"
                                title="Delete"
                                aria-label="Delete"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-500 shrink-0" />
                              </button>
                              */}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Table Footer: Pagination & Load More */}
          {!loading && filteredTrips.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/70 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-xs text-gray-500 font-medium">
                Showing <span className="font-semibold text-gray-900">{filteredTrips.length}</span> of{' '}
                <span className="font-semibold text-gray-900">{effectiveTotal}</span> trips
                {hasMoreTrips && (
                  <span className="text-gray-400 ml-1.5">
                    ({effectiveTotal - filteredTrips.length} more available)
                  </span>
                )}
              </div>

              {hasMoreTrips ? (
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full bg-white border border-blue-300 hover:border-blue-500 hover:bg-blue-50/60 text-blue-600 text-xs font-semibold shadow-xs transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" />
                      <span>Loading more trips…</span>
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 text-blue-600" />
                      <span>
                        Load More Trips (+{Math.min(pageSize, effectiveTotal - filteredTrips.length)})
                      </span>
                    </>
                  )}
                </button>
              ) : (
                <div className="inline-flex items-center gap-1.5 text-xs text-gray-400 font-medium py-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  <span>All {effectiveTotal} trips loaded</span>
                </div>
              )}
            </div>
          )}
          </>
        )}
      </div>

      {viewingTrip?.driver && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setViewingTrip(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Driver &amp; trip details</h3>
              <button
                onClick={() => setViewingTrip(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-3">
              {[
                ['Name', viewingTrip.driver.name, false],
                ['Phone', viewingTrip.driver.phone, true],
                ['Assigned Vehicle', `${viewingTrip.driver.vehiclePlate} / ${viewingTrip.driver.vehicleType}`, false],
                ['Verified', viewingTrip.driver.isVerified ? 'Yes' : 'Pending review', false],
                viewingTrip.driver.offPlatform
                  ? ['Live Tracking', 'Not available — driver not using the app', false]
                  : ['Live Tracking', 'Available via the driver app', false],
              ].map(([label, value, isPhone]) => (
                <div key={label} className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{label}</span>
                  {isPhone ? (
                    <CopyablePhone
                      phone={value}
                      callLabel="Call driver"
                      showIcon={false}
                      iconSize="w-3.5 h-3.5"
                      textColor="text-blue-600 hover:text-blue-800 font-medium"
                      callClassName="hover:underline"
                    />
                  ) : (
                    <span className="font-medium text-gray-900">{value}</span>
                  )}
                </div>
              ))}
              {/* Stage-specific Timer (Towards Pickup, Wait at Pickup, Towards Drop, Wait at Drop) */}
              {(() => {
                const stageTimer = getActiveStageTimerConfig(viewingTrip);
                if (!stageTimer) return null;
                return (
                <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{stageTimer.label.replace(':', '')}</span>
                    <SlaTimer
                      startAt={stageTimer.startAt}
                      warnAmberSeconds={stageTimer.warnAmberSeconds}
                      warnRedSeconds={stageTimer.warnRedSeconds}
                      label="Stage SLA"
                    />
                </div>
                );
              })()}
              {/* Total Trip SLA Timer for Active Stages */}
              {['Towards Pickup', 'At Pickup', 'Towards Drop', 'At Drop', 'Untracked'].includes(viewingTrip.stage) && getTripStartTime(viewingTrip) && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 font-medium">Total Trip SLA</span>
                  <SlaTimer
                    startAt={getTripStartTime(viewingTrip)}
                    warnAmberSeconds={45 * 60}
                    warnRedSeconds={60 * 60}
                    label="Trip SLA"
                  />
                </div>
              )}
              {/* Completed Stage: Total Duration */}
              {viewingTrip.stage === 'Completed' && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 font-medium">Total Trip Duration</span>
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium tabular-nums ${
                      (viewingTrip.actualDurationMinutes || 0) >= 60
                        ? 'bg-red-50 text-red-600 border border-red-200 font-semibold'
                        : (viewingTrip.actualDurationMinutes || 0) >= 45
                        ? 'bg-amber-50 text-amber-700 border border-amber-300 font-medium'
                        : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    }`}
                  >
                    <Clock className="w-3 h-3" />
                    {formatDuration(viewingTrip.actualDurationMinutes)}
                  </span>
                </div>
              )}
              {/* Stage Breakdown for completed stages */}
              {(() => {
                const completedLegs = getTripLegBreakdown(viewingTrip).filter((l) => !l.isLive);
                if (completedLegs.length === 0) return null;
                return (
                  <div className="pt-2 border-t border-gray-100 space-y-1.5">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-400">Completed Stages</div>
                    {completedLegs.map((leg) => (
                      <div key={leg.label} className="flex items-center justify-between text-xs text-gray-600">
                        <span>{leg.label}</span>
                        <span className="font-medium text-gray-800">{formatDuration(leg.minutes)}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
              {/* Scheduled Stage */}
              {viewingTrip.stage === 'Scheduled' && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 font-medium">Trip Timing</span>
                  <span className="text-slate-600 text-xs font-medium">
                    Scheduled for {viewingTrip.scheduledDatetime ? new Date(viewingTrip.scheduledDatetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {infoTrip && (
        <CommandCenterTripDetailsModal
          trip={infoTrip}
          onClose={() => setInfoTrip(null)}
          onAssignDriver={(t) => setDriverModal({ trip: t, mode: 'assign' })}
          onViewMap={(t) => setViewingMapTrip(t)}
          onCancelBooking={(t) => setCancelModal({ trip: t })}
          onPaymentLinkPaid={refreshActiveStage}
          onBookingUpdated={refreshActiveStage}
        />
      )}

      {viewingMapTrip && (
        <CommandCenterTripMap
          trip={viewingMapTrip}
          onClose={() => setViewingMapTrip(null)}
          onOpenDetails={(t) => {
            setViewingMapTrip(null);
            setInfoTrip(t);
          }}
        />
      )}

      {driverModal && (
        <CommandCenterAssignDriverModal
          trip={driverModal.trip}
          mode={driverModal.mode}
          currentDriver={driverModal.trip.driver}
          onClose={() => setDriverModal(null)}
          onError={onError}
          onAssigned={(driver, reason) => {
            handleAction(
              reason
                ? `Changed driver on #${driverModal.trip.id} to ${driver.name} — reason: ${reason}`
                : `Assigned ${driver.name} to #${driverModal.trip.id}`
            );
            setDriverModal(null);
            refreshActiveStage();
          }}
        />
      )}

      {stageModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => { if (!transitioningTripId) { setStageModal(null); setStageModalNotes(''); } }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Activity className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Update Trip Stage
                  </h3>
                  <p className="text-xs text-gray-500">
                    Trip #{stageModal.trip.id} &bull; {stageModal.trip.customer?.name}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { if (!transitioningTripId) { setStageModal(null); setStageModalNotes(''); } }}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Visual Stage Transition Indicator */}
              <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5 flex items-center justify-between gap-3">
                <div className="flex-1 text-center">
                  <span className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                    Current Stage
                  </span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${STATUS_BADGE_CLASS[stageModal.currentStage] || 'bg-gray-100 text-gray-700'}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />
                    {stageModal.currentStage}
                  </span>
                </div>

                <div className="flex flex-col items-center justify-center text-gray-400 px-1">
                  <ArrowRight className="w-4 h-4 text-blue-500" />
                </div>

                <div className="flex-1 text-center">
                  <span className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                    Next Step
                  </span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${STATUS_BADGE_CLASS[stageModal.targetStage] || 'bg-blue-100 text-blue-700'}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />
                    {stageModal.targetStage}
                  </span>
                </div>
              </div>

              {/* Select Target Step Section */}
              {stageModal.allowedTargets && stageModal.allowedTargets.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold tracking-wide text-gray-700 uppercase">
                      Select Next Step
                    </label>
                    <span className="text-[11px] text-gray-400">
                      Choose which step to move to
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {stageModal.allowedTargets.map((target) => {
                      const isSelected = stageModal.targetStage === target;
                      return (
                        <button
                          key={target}
                          type="button"
                          onClick={() => setStageModal(prev => ({ ...prev, targetStage: target }))}
                          className={`flex items-center justify-between p-2.5 rounded-xl border text-xs font-medium transition-all cursor-pointer ${
                            isSelected
                              ? 'bg-blue-600 text-white border-blue-600 shadow-sm font-semibold'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${isSelected ? 'bg-white' : 'bg-gray-400'}`} />
                            <span className="truncate">{target}</span>
                          </div>
                          {isSelected ? (
                            <CheckCircle2 className="w-4 h-4 text-white shrink-0" />
                          ) : (
                            <span className="w-3.5 h-3.5 rounded-full border border-gray-300 shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Driver / Vehicle info snippet */}
              <div className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 border border-gray-100 text-xs text-gray-600">
                <div className="flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-gray-400" />
                  <span>Driver: <strong className="text-gray-800">{stageModal.trip.driver?.name || 'Unassigned'}</strong></span>
                </div>
                {stageModal.trip.driver?.vehicleNumber && (
                  <div className="flex items-center gap-1 text-gray-500">
                    <Ambulance className="w-3.5 h-3.5 text-gray-400" />
                    <span>{stageModal.trip.driver.vehicleNumber}</span>
                  </div>
                )}
              </div>

              {/* Dispatcher Notes */}
              <div>
                <label className="block text-xs font-semibold tracking-wide text-gray-500 mb-1">
                  DISPATCHER NOTES (OPTIONAL)
                </label>
                <textarea
                  value={stageModalNotes}
                  onChange={(e) => setStageModalNotes(e.target.value)}
                  rows={2}
                  placeholder="e.g. Driver arrived at drop / Handover completed"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button
                type="button"
                onClick={() => { if (!transitioningTripId) { setStageModal(null); setStageModalNotes(''); } }}
                disabled={!!transitioningTripId}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-white text-xs font-medium disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleTransitionStage(stageModal.trip, stageModal.targetStage, stageModalNotes)}
                disabled={!stageModal.targetStage || !!transitioningTripId}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium disabled:opacity-50 transition-colors shadow-sm"
              >
                {transitioningTripId === stageModal.trip.id && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                <span>{transitioningTripId === stageModal.trip.id ? 'Advancing Stage…' : `Confirm & Move to ${stageModal.targetStage}`}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => { if (!cancelling) { setCancelModal(null); setCancelReason(''); } }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Cancel booking #{cancelModal.trip.id}?</h3>
              <button
                onClick={() => { if (!cancelling) { setCancelModal(null); setCancelReason(''); } }}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-sm text-gray-500">
                {cancelModal.trip.driver
                  ? `This will free up ${cancelModal.trip.driver.name} and their vehicle, and notify them the trip is off.`
                  : 'This closes the booking out of the pipeline. This cannot be undone.'}
              </p>
              <div>
                <label className="block text-xs font-semibold tracking-wide text-gray-400 mb-1.5">
                  REASON <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. Patient called back, no longer needed"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => { setCancelModal(null); setCancelReason(''); }}
                disabled={cancelling}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium disabled:opacity-50"
              >
                Keep Booking
              </button>
              <button
                onClick={handleCancelBooking}
                disabled={!cancelReason.trim() || cancelling}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50"
              >
                {cancelling && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {cancelling ? 'Cancelling…' : 'Cancel Booking'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => { if (!deleting) setDeleteModal(null); }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Delete booking #{deleteModal.trip.id}?</h3>
              <button
                onClick={() => { if (!deleting) setDeleteModal(null); }}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-sm text-gray-500">
                This removes <span className="font-medium text-gray-900">#{deleteModal.trip.id}</span> ({deleteModal.trip.customer.name}) from every Trips tab — meant for clearing out test bookings, not real ones. It won't be freeing up the driver/vehicle or notifying anyone (use Cancel for a real trip instead).
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setDeleteModal(null)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium disabled:opacity-50"
              >
                Keep Booking
              </button>
              <button
                onClick={handleDeleteBooking}
                disabled={deleting}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50"
              >
                {deleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {deleting ? 'Deleting…' : 'Delete Booking'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommandCenterTrackingSimulator;
